import { mcpClient, getMcpProjectId } from '../lib/client';
import { TEMPLATES_ENDPOINTS } from './templates.utils';
import type { 
    Template, 
    TemplatesListResponse, 
    ListTemplatesParams,
    CreateFlowRequest,
    CreateFlowResponse,
    ImportFlowRequest,
    ImportFlowResponse
} from './templates.types';
import type { ApiResponse } from '../lib/types';

// Helper type for error handling
type ApiError = {
    response?: {
        data?: {
            message?: string;
        };
        status?: number;
    };
};

/**
 * Fetches all templates using the mcpClient (Activepieces API)
 * Uses type=CUSTOM to get platform-specific templates
 */
export const getTemplates = async (params?: ListTemplatesParams): Promise<ApiResponse<TemplatesListResponse>> => {
    try {
        console.log('[Templates API] Fetching templates');
        
        // Build query params
        const queryParams = new URLSearchParams();
        
        // Default to CUSTOM type for platform templates
        queryParams.append('type', params?.type || 'CUSTOM');
        
        if (params?.search) {
            queryParams.append('search', params.search);
        }
        if (params?.category) {
            queryParams.append('category', params.category);
        }
        if (params?.cursor) {
            queryParams.append('cursor', params.cursor);
        }
        if (params?.limit) {
            queryParams.append('limit', params.limit.toString());
        }
        if (params?.pieces && params.pieces.length > 0) {
            params.pieces.forEach(piece => queryParams.append('pieces', piece));
        }
        if (params?.tags && params.tags.length > 0) {
            params.tags.forEach(tag => queryParams.append('tags', tag));
        }
        
        const response = await mcpClient.get(`${TEMPLATES_ENDPOINTS.TEMPLATES}?${queryParams.toString()}`);
        
        if (response.data) {
            console.log(`[Templates API] Success, received ${response.data.data?.length || 0} templates`);
            return { data: response.data };
        } else {
            return { data: { data: [], next: null, previous: null } };
        }
    } catch (error: unknown) {
        console.error('[Templates API] Error fetching templates:', error);
        return { 
            error: (error as ApiError).response?.data?.message || (error as Error).message || 'Failed to fetch templates',
            status: (error as ApiError).response?.status || 500 
        };
    }
};

/**
 * Fetches a single template by ID using the mcpClient
 */
export const getTemplateById = async (templateId: string): Promise<ApiResponse<Template>> => {
    try {
        console.log('[Templates API] Fetching template:', templateId);
        
        const response = await mcpClient.get(TEMPLATES_ENDPOINTS.TEMPLATE_BY_ID(templateId));
        
        if (response.data) {
            console.log('[Templates API] Success, received template:', response.data.name);
            return { data: response.data };
        } else {
            throw new Error('Template not found');
        }
    } catch (error: unknown) {
        console.error('[Templates API] Error fetching template:', error);
        return { 
            error: (error as ApiError).response?.data?.message || (error as Error).message || 'Failed to fetch template',
            status: (error as ApiError).response?.status || 500 
        };
    }
};

/**
 * Creates a new empty flow using the mcpClient
 */
export const createFlow = async (request: CreateFlowRequest): Promise<ApiResponse<CreateFlowResponse>> => {
    try {
        console.log('[Templates API] Creating flow:', request.displayName);
        
        const response = await mcpClient.post(TEMPLATES_ENDPOINTS.FLOWS, {
            displayName: request.displayName,
            projectId: request.projectId,
            ...(request.folderId && { folderId: request.folderId })
        });
        
        if (response.data) {
            console.log('[Templates API] Flow created:', response.data.id);
            return { data: response.data };
        } else {
            throw new Error('Failed to create flow');
        }
    } catch (error: unknown) {
        console.error('[Templates API] Error creating flow:', error);
        return { 
            error: (error as ApiError).response?.data?.message || (error as Error).message || 'Failed to create flow',
            status: (error as ApiError).response?.status || 500 
        };
    }
};

/**
 * Imports a template into an existing flow using the mcpClient
 */
export const importFlowFromTemplate = async (flowId: string, request: ImportFlowRequest): Promise<ApiResponse<ImportFlowResponse>> => {
    try {
        console.log('[Templates API] Importing template into flow:', flowId);
        
        const response = await mcpClient.post(TEMPLATES_ENDPOINTS.FLOW_BY_ID(flowId), request);
        
        if (response.data) {
            console.log('[Templates API] Template imported successfully');
            return { data: response.data };
        } else {
            throw new Error('Failed to import template');
        }
    } catch (error: unknown) {
        console.error('[Templates API] Error importing template:', error);
        return { 
            error: (error as ApiError).response?.data?.message || (error as Error).message || 'Failed to import template',
            status: (error as ApiError).response?.status || 500 
        };
    }
};

/**
 * Creates a flow from a template (combines createFlow + importFlowFromTemplate)
 * This is the main function to use when user clicks "Use Template"
 */
export const createFlowFromTemplate = async (
    template: Template, 
    projectId: string,
    folderId?: string | null
): Promise<ApiResponse<ImportFlowResponse>> => {
    try {
        console.log('[Templates API] Creating flow from template:', template.name);
        
        // Get the first flow from the template (templates can have multiple flows)
        const templateFlow = template.flows[0];
        if (!templateFlow) {
            throw new Error('Template has no flows');
        }
        
        // Step 1: Create empty flow
        const createResult = await createFlow({
            displayName: templateFlow.displayName || template.name,
            projectId,
            folderId
        });
        
        if (createResult.error || !createResult.data) {
            throw new Error(createResult.error || 'Failed to create flow');
        }
        
        const flowId = createResult.data.id;
        
        // Step 2: Import template into the created flow
        const importRequest: ImportFlowRequest = {
            type: 'IMPORT_FLOW',
            request: {
                displayName: templateFlow.displayName || template.name,
                trigger: templateFlow.trigger,
                schemaVersion: templateFlow.schemaVersion
            }
        };
        
        const importResult = await importFlowFromTemplate(flowId, importRequest);
        
        if (importResult.error || !importResult.data) {
            throw new Error(importResult.error || 'Failed to import template');
        }
        
        console.log('[Templates API] Flow created from template successfully:', importResult.data.id);
        return { data: importResult.data };
        
    } catch (error: unknown) {
        console.error('[Templates API] Error creating flow from template:', error);
        return { 
            error: (error as Error).message || 'Failed to create flow from template',
            status: 500 
        };
    }
};

/**
 * Gets the current project ID from the mcpClient authentication cache
 * The mcpClient interceptor fetches and caches this during authentication
 */
export const getCurrentProjectId = async (): Promise<string | null> => {
    try {
        // Use the exported helper from client.ts that accesses the MCP auth cache
        return await getMcpProjectId();
    } catch (error) {
        console.error('[Templates API] Error getting project ID:', error);
        return null;
    }
};
